import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Webhook settings — register HTTPS endpoints that TraqPack streams events to.
 *
 * Everything goes through the mcp_* RPCs rather than the tables directly, so
 * the portal and the MCP tools enforce exactly the same rules: one definition
 * of who may see or change what, and a signing secret that is returned once at
 * creation and never readable afterwards.
 */

const EVENT_TYPES = [
  { value: '*', label: 'Everything', hint: 'Every event, including types added later' },
  { value: 'position.reported', label: 'Positions', hint: 'Each GPS, Wi-Fi or cell fix' },
  { value: 'alert.raised', label: 'Alerts', hint: 'Battery, temperature, offline, speed' },
  { value: 'geofence.enter', label: 'Geofence entry', hint: 'A device entered a zone' },
  { value: 'geofence.exit', label: 'Geofence exit', hint: 'A device left a zone' },
];

interface Endpoint {
  id: string;
  customer_name: string | null;
  url: string;
  description: string | null;
  event_types: string[];
  is_active: boolean;
  health: string;
  consecutive_failures: number;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error: string | null;
  pending_deliveries: number;
  created_at: string;
}

interface Delivery {
  delivery_id: number;
  event_type: string;
  status: string;
  attempts: number;
  last_status_code: number | null;
  last_error: string | null;
  next_attempt_at: string | null;
  created_at: string;
  delivered_at: string | null;
}

const HEALTH_STYLES: Record<string, string> = {
  healthy: 'bg-green-100 text-green-800',
  failing: 'bg-amber-100 text-amber-800',
  unhealthy: 'bg-red-100 text-red-800',
};

function WebhookSettings() {
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['*']);
  const [saving, setSaving] = useState(false);

  // Shown once, immediately after creation, then discarded from memory on reload.
  const [newSecret, setNewSecret] = useState<{ url: string; secret: string } | null>(null);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.rpc('mcp_list_webhooks', { p_customer: null });
    if (e) setError(e.message);
    else { setEndpoints((data as Endpoint[]) ?? []); setError(null); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const { data, error: err } = await supabase.rpc('mcp_create_webhook', {
      p_url: url.trim(),
      p_description: description.trim() || null,
      p_event_types: selectedTypes.length ? selectedTypes : ['*'],
      p_customer: null,
    });
    if (err) {
      setError(err.message);
    } else {
      const row = (data as any[])?.[0];
      if (row?.secret) setNewSecret({ url: row.url, secret: row.secret });
      setUrl(''); setDescription(''); setSelectedTypes(['*']); setShowForm(false);
      await load();
    }
    setSaving(false);
  };

  const toggle = async (ep: Endpoint) => {
    const { error: e } = await supabase.rpc('mcp_update_webhook', {
      p_id: ep.id, p_is_active: !ep.is_active, p_event_types: null, p_description: null,
    });
    if (e) setError(e.message);
    await load();
  };

  const remove = async (ep: Endpoint) => {
    // Deleting drops queued deliveries too, so make that explicit.
    if (!window.confirm(`Delete this webhook?\n\n${ep.url}\n\nQueued events for it will be discarded.`)) return;
    const { error: e } = await supabase.rpc('mcp_delete_webhook', { p_id: ep.id });
    if (e) setError(e.message);
    await load();
  };

  const openDeliveries = async (ep: Endpoint) => {
    if (expanded === ep.id) { setExpanded(null); return; }
    setExpanded(ep.id);
    setDeliveriesLoading(true);
    const { data } = await supabase.rpc('mcp_webhook_deliveries', {
      p_id: ep.id, p_limit: 15, p_status: null,
    });
    setDeliveries((data as Delivery[]) ?? []);
    setDeliveriesLoading(false);
  };

  const replay = async (ep: Endpoint) => {
    const { data, error: e } = await supabase.rpc('mcp_replay_webhook', { p_id: ep.id, p_hours: 24 });
    if (e) setError(e.message);
    else window.alert(`Requeued ${(data as any[])?.[0]?.requeued ?? 0} delivery(ies).`);
    await load();
  };

  const toggleType = (value: string) => {
    if (value === '*') { setSelectedTypes(['*']); return; }
    setSelectedTypes((prev) => {
      const without = prev.filter((t) => t !== '*');
      return without.includes(value) ? without.filter((t) => t !== value) : [...without, value];
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Webhooks</h1>
            <p className="mt-1 text-gray-600">
              Stream your tracking events to your own systems as they happen.
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="shrink-0 rounded-lg bg-black text-white px-4 py-2 font-medium hover:bg-gray-800"
            >
              Add endpoint
            </button>
          )}
        </div>

        {newSecret && (
          <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-5">
            <h2 className="font-semibold text-gray-900">Save your signing secret</h2>
            <p className="mt-1 text-sm text-gray-700">
              This is shown once and cannot be retrieved later — only replaced. Use it to verify
              that requests to <span className="font-mono">{newSecret.url}</span> really came from us.
            </p>
            <code className="mt-3 block break-all rounded-lg bg-white border border-amber-200 p-3 text-sm">
              {newSecret.secret}
            </code>
            <p className="mt-3 text-xs text-gray-600">
              Each request carries <span className="font-mono">traqpack-signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</span>.
              Recompute it as HMAC-SHA256 of <span className="font-mono">"&lt;t&gt;.&lt;raw body&gt;"</span> with this
              secret and compare. Reject anything where <span className="font-mono">t</span> is old — that is what
              stops an old request being replayed at you.
            </p>
            <button onClick={() => setNewSecret(null)} className="mt-4 text-sm font-medium underline">
              I have saved it
            </button>
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        {showForm && (
          <form onSubmit={create} className="mt-6 rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="font-semibold text-gray-900">New endpoint</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="wh-url" className="block text-sm font-medium text-gray-700">Endpoint URL</label>
                <input
                  id="wh-url" type="url" required value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/hooks/traqpack"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be HTTPS. Private and loopback addresses are rejected.
                </p>
              </div>
              <div>
                <label htmlFor="wh-desc" className="block text-sm font-medium text-gray-700">Description</label>
                <input
                  id="wh-desc" type="text" value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Warehouse data pipeline"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700">Send</legend>
                <div className="mt-2 space-y-2">
                  {EVENT_TYPES.map((t) => (
                    <label key={t.value} className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox" checked={selectedTypes.includes(t.value)}
                        onChange={() => toggleType(t.value)} className="mt-1"
                      />
                      <span>
                        <span className="font-medium text-gray-900">{t.label}</span>
                        <span className="text-gray-500"> — {t.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button" onClick={() => { setShowForm(false); setError(null); }}
                className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit" disabled={saving}
                className="rounded-lg bg-black text-white px-4 py-2 font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create endpoint'}
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 space-y-4">
          {loading && <p className="text-gray-600">Loading…</p>}

          {!loading && endpoints.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
              <p className="text-gray-900 font-medium">No endpoints yet</p>
              <p className="mt-1 text-sm text-gray-600">
                Add one and every event from your devices will be posted to it as it happens.
              </p>
            </div>
          )}

          {endpoints.map((ep) => (
            <div key={ep.id} className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${HEALTH_STYLES[ep.health] ?? 'bg-gray-100 text-gray-700'}`}>
                      {ep.health}
                    </span>
                    {!ep.is_active && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">paused</span>
                    )}
                    {ep.pending_deliveries > 0 && (
                      <span className="text-xs text-gray-500">{ep.pending_deliveries} queued</span>
                    )}
                  </div>
                  <p className="mt-2 break-all font-mono text-sm text-gray-900">{ep.url}</p>
                  {ep.description && <p className="text-sm text-gray-600">{ep.description}</p>}
                  <p className="mt-1 text-xs text-gray-500">
                    {ep.event_types.includes('*') ? 'All events' : ep.event_types.join(', ')}
                  </p>
                  {ep.last_error && (
                    <p className="mt-2 text-xs text-red-600 break-all">Last error: {ep.last_error}</p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => openDeliveries(ep)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
                    {expanded === ep.id ? 'Hide' : 'Deliveries'}
                  </button>
                  <button onClick={() => toggle(ep)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
                    {ep.is_active ? 'Pause' : 'Resume'}
                  </button>
                  <button onClick={() => remove(ep)} className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50">
                    Delete
                  </button>
                </div>
              </div>

              {expanded === ep.id && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  {deliveriesLoading && <p className="text-sm text-gray-600">Loading…</p>}
                  {!deliveriesLoading && deliveries.length === 0 && (
                    <p className="text-sm text-gray-600">Nothing delivered yet.</p>
                  )}
                  {!deliveriesLoading && deliveries.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="text-xs uppercase text-gray-500">
                          <tr>
                            <th className="py-1 pr-4">Event</th>
                            <th className="py-1 pr-4">Status</th>
                            <th className="py-1 pr-4">Tries</th>
                            <th className="py-1 pr-4">Code</th>
                            <th className="py-1">When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveries.map((d) => (
                            <tr key={d.delivery_id} className="border-t border-gray-100">
                              <td className="py-1.5 pr-4 font-mono text-xs">{d.event_type}</td>
                              <td className="py-1.5 pr-4">{d.status}</td>
                              <td className="py-1.5 pr-4">{d.attempts}</td>
                              <td className="py-1.5 pr-4">{d.last_status_code ?? '—'}</td>
                              <td className="py-1.5 text-gray-600">
                                {new Date(d.delivered_at ?? d.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {ep.health !== 'healthy' && (
                    <button onClick={() => replay(ep)} className="mt-4 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
                      Replay failed deliveries
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WebhookSettings;
